import React, { useEffect, useMemo, useState } from "react";
import { Drawer, Empty, Input, Spin } from "antd";
import {
  CloseOutlined,
  MinusOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { isFaqEnabled, listPublishedFaqs } from "./faqApi";
import { getApiErrorMessage } from "../../utils/apiError";
import { faqAnswerPlainText, sanitizeFaqHtml } from "./sanitizeFaqHtml";
import { useFaqPanelSize } from "./faqLayout";
import "./faq.css";

const FaqDrawer = ({ open, onClose, token, isDarkMode = false }) => {
  const enabled = isFaqEnabled();
  const { drawerWidth } = useFaqPanelSize();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    if (!open || !enabled || !token) {
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError("");
    listPublishedFaqs(token)
      .then((data) => {
        if (!cancelled) {
          setItems(data);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setItems([]);
          setError(getApiErrorMessage(err, "Unable to load FAQs."));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, enabled, token]);

  useEffect(() => {
    if (!open) {
      setSearch("");
      setActiveId(null);
    }
  }, [open]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return items;
    }
    return items.filter((item) =>
      `${item.question} ${faqAnswerPlainText(item.answer)}`.toLowerCase().includes(query)
    );
  }, [items, search]);

  return (
    <Drawer
      title={null}
      closable={false}
      placement="right"
      open={open}
      onClose={onClose}
      width={drawerWidth}
      rootClassName="faq-drawer-root"
      className={`faq-drawer${isDarkMode ? " is-dark" : ""}`}
      styles={{
        header: { display: "none" },
        body: { padding: 0 },
      }}
      destroyOnHidden
    >
      <div className="faq-shell">
        <header className="faq-hero">
          <div className="faq-hero-top">
            <span className="faq-kicker">Help center</span>
            <button
              type="button"
              className="faq-close"
              onClick={onClose}
              aria-label="Close FAQ"
            >
              <CloseOutlined />
            </button>
          </div>
          <h2 className="faq-title">Frequently asked questions</h2>
          <p className="faq-hero-copy">
            Browse answers without leaving the page.
          </p>
          <Input
            allowClear
            size="middle"
            prefix={<SearchOutlined />}
            placeholder="Search questions"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setActiveId(null);
            }}
            className="faq-search"
          />
        </header>

        <div className="faq-body">
          {loading ? (
            <div className="faq-drawer-loading">
              <Spin />
            </div>
          ) : error ? (
            <Empty description={error} />
          ) : filtered.length === 0 ? (
            <Empty description={search ? "No matching FAQs" : "No FAQs yet"} />
          ) : (
            <>
              <div className="faq-count">
                {filtered.length} {filtered.length === 1 ? "question" : "questions"}
              </div>
              <div className="faq-list">
                {filtered.map((item, index) => {
                  const isOpen = activeId === item.id;
                  return (
                    <article
                      key={item.id}
                      className={`faq-card${isOpen ? " is-open" : ""}`}
                    >
                      <button
                        type="button"
                        className="faq-card-trigger"
                        aria-expanded={isOpen}
                        onClick={() => setActiveId(isOpen ? null : item.id)}
                      >
                        <span className="faq-index">
                          {String(index + 1).padStart(2, "0")}
                        </span>
                        <span className="faq-question">{item.question}</span>
                        <span className="faq-toggle" aria-hidden>
                          {isOpen ? <MinusOutlined /> : <PlusOutlined />}
                        </span>
                      </button>
                      {isOpen ? (
                        /<[a-z][\s\S]*>/i.test(item.answer || "") ? (
                          <div
                            className="faq-answer"
                            dangerouslySetInnerHTML={{ __html: sanitizeFaqHtml(item.answer) }}
                          />
                        ) : (
                          <div className="faq-answer faq-answer--plain">{item.answer}</div>
                        )
                      ) : null}
                    </article>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default FaqDrawer;
